import { useEffect, useRef } from 'react';

export const DISCARD_CHANGES_PROMPT = 'Discard your changes?';

export interface EscapeToCloseOptions {
  /** Listen for Escape only while true (e.g. dialog open and not busy). Default true. */
  enabled?: boolean;
  /** When true (or a function returning true), ask before closing. */
  confirmIfDirty?: boolean | (() => boolean);
}

/**
 * Closes a dialog when the user presses Escape. Callbacks are read from refs,
 * so callers can pass inline functions without re-registering the listener.
 */
export function useEscapeToClose(onClose: () => void, opts: EscapeToCloseOptions = {}): void {
  const { enabled = true, confirmIfDirty = false } = opts;
  const onCloseRef = useRef(onClose);
  const dirtyRef = useRef(confirmIfDirty);
  onCloseRef.current = onClose;
  dirtyRef.current = confirmIfDirty;

  useEffect(() => {
    if (!enabled) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      const dirty = typeof dirtyRef.current === 'function' ? dirtyRef.current() : dirtyRef.current;
      if (dirty && !window.confirm(DISCARD_CHANGES_PROMPT)) return;
      onCloseRef.current();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [enabled]);
}

export default useEscapeToClose;
